/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/store/helpers';
import {PageState} from '@ion/applications/src/types/pages';
import {
  AlertNotification
} from '@ion/core/src/types/entities';
import {LoadNotificationEventsParams, ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageInitializeAction} from '@/types/redux';

// defines the page state...
export interface NotificationEventsState extends PageState {
  notificationEvents: {
    error: ServiceError;
    loading: boolean;
    notificationEventParams: LoadNotificationEventsParams | null;
    acknowledgedError: ServiceError;
    refresh: boolean;
    notifications: number;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {}
  },
  view: AlertNotification,
  bulkAcknowledge: {
    isEditing: boolean,
    selected: LoadNotificationEventsParams,
    loading: boolean,
    error: ServiceError,
    result: LoadNotificationEventsParams | null;
  }
};

// name of reducer
export const NAME = 'notificationEvents';

// defines the user preference key for the notification events page ...
const NOTIFICATION_PREFERENCE = 'notification-events';

// grid related actions types...
export const INITIALIZE_NOTIFICATION_EVENTS          = 'INITIALIZE_NOTIFICATION_EVENTS';
export const SET_NOTIFICATION_EVENTS_GRID_COLUMNS    = 'SET_NOTIFICATION_EVENTS_GRID_COLUMNS';
export const UPDATE_NOTIFICATION_EVENTS_GRID_COLUMNS = 'UPDATE_NOTIFICATION_EVENTS_GRID_COLUMNS';
export const SET_NOTIFICATION_EVENTS_FILTER_MODEL    = 'SET_NOTIFICATION_EVENTS_FILTER_MODEL';

// view event actions...
export const VIEW_NOTIFICATION_EVENTS = 'VIEW_NOTIFICATION_EVENTS';

// Acknowledge Notifications Errors
export const SET_ACKNOWLEDGE_ERROR      = 'SET_ACKNOWLEDGE_ERROR';
export const SET_BULK_ACKNOWLEDGE_ERROR = 'SET_BULK_ACKNOWLEDGE_ERROR';

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_NOTIFICATION_EVENTS_FILTER_MODEL, model
});

// invokes when the page mounts initially...
export const initializeNotificationEvents = (): PageInitializeAction => ({
  type : INITIALIZE_NOTIFICATION_EVENTS
});

export const viewNotificationEvents = (event: any) => ({type: VIEW_NOTIFICATION_EVENTS, event});

// defines the initial state for the reducer ...
export const initialState: NotificationEventsState = {
  isInitialized      : false,
  notificationEvents : {
    error                   : null,
    acknowledgedError       : null,
    loading                 : false,
    notificationEventParams : null,
    refresh                 : false,
    notifications           : null
  },
  view : null,
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {
      isAcknowledged : {
        filterType : 'text',
        type       : 'equals',
        filter     : 'No'
      }
    }
  },
  bulkAcknowledge : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  }
};
    
// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_NOTIFICATION_EVENTS] : draft => {
    draft.isInitialized  = true;
    const notificationEventsPreference  = getGridColumnState(NOTIFICATION_PREFERENCE, true);
    draft.grid.columnState              = notificationEventsPreference;
    draft.grid.preferedColumnState      = notificationEventsPreference;
  },
  [SET_NOTIFICATION_EVENTS_GRID_COLUMNS] : ({grid}: NotificationEventsState, {columnState}) => {
    onSetGridColumns({
      preference : NAME,
      isLoading  : false,
      grid
    }, columnState);
  },
  [UPDATE_NOTIFICATION_EVENTS_GRID_COLUMNS] : ({grid}: NotificationEventsState, {columnState}) => {
    onUpdateGridColumns({
      preference : NOTIFICATION_PREFERENCE,
      grid
    }, columnState);
  },
  [VIEW_NOTIFICATION_EVENTS] : (draft: NotificationEventsState, {event}) => {
    draft.view = event;
  },
  [SET_ACKNOWLEDGE_ERROR] : (draft: NotificationEventsState, {acknowledgeError}) => {
    draft.notificationEvents.acknowledgedError = acknowledgeError;
  },
  [SET_BULK_ACKNOWLEDGE_ERROR] : (draft: NotificationEventsState, {bulkAcknowledgeError}) => {
    draft.bulkAcknowledge.error = bulkAcknowledgeError;
  },
  [SET_NOTIFICATION_EVENTS_FILTER_MODEL] : ({grid}: NotificationEventsState, {model}) => {
    grid.filterModel = model;
  }
};

/**
 * @acknowledgeError
 *
 * Invoked when there is error in notification event acknowledgement.
 */
export const acknowledgeError = (acknowledgeError: any) => (
  {type: SET_ACKNOWLEDGE_ERROR, acknowledgeError}
);

/**
 * @bulkAcknowledgeError
 *
 * Invoked when there is error in bulk notification event acknowledgement.
 */
export const bulkAcknowledgeError = (bulkAcknowledgeError: any) => (
  {type: SET_BULK_ACKNOWLEDGE_ERROR, bulkAcknowledgeError}
);

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: NotificationEventsState = initialState, action) => {
  reducers[action.type]?.(draft, action);
  return draft;
});
